import { useMemo } from 'react'
import PageContainer from '@/common/components/PageContainer'
import withUserPermission from '@/common/components/WithUserPermission'
import { PermissionType } from '@/common/types'
import { PermissionKeys } from '@/modules/manage-member/mappers'
import PlayLogsListing from '../../components/PlayLogs/components/PlayLogsListing'
import useQueryGetPlayLogs from '../../api/hooks/playlogs/useQueryGetPlayLogs'
import useQueryGetGameProviders from '../../api/hooks/playlogs/useQueryGetGameProviders'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { parseAsString, useQueryState } from 'nuqs'
import type { GetPlayLogsParams } from '@/modules/dashboard/type'

function PlayLogsPage() {
	const { currentPage, pageSize, searchQuery } = useTablePagination()
	const { dateFilter } = useDateFilter()
	const [providerName] = useQueryState(
		'providerName',
		parseAsString.withOptions({ shallow: false }).withDefault(''),
	)

	// Build query params for playlogs
	const playLogsParams: GetPlayLogsParams = {
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
		phone: searchQuery || undefined,
		provider: providerName || undefined,
		startDate: dateFilter.startDate || undefined,
		endDate: dateFilter.endDate || undefined,
	}

	// Fetch playlogs
	const { data: playLogsData, isLoading: isPlayLogsLoading } =
		useQueryGetPlayLogs(playLogsParams)

	// Fetch providers for dropdown (get all providers)
	const { data: providersData, isLoading: isProvidersLoading } =
		useQueryGetGameProviders({
			take: 100,
			skip: 0,
		})

	// Transform providers data to options format
	const providerOptions = useMemo(() => {
		if (!providersData?.data) return []
		return providersData.data.map((provider) => ({
			value: provider.name,
			label: provider.name,
		}))
	}, [providersData])

	return (
		<PageContainer>
			<PlayLogsListing
				data={playLogsData?.data || []}
				total={playLogsData?.total || 0}
				isLoading={isPlayLogsLoading || isProvidersLoading}
				providerOptions={providerOptions}
			/>
		</PageContainer>
	)
}

const GuardedPlayLogsPage = withUserPermission(PlayLogsPage, {
	permissionType: PermissionType.Read,
	permissionKey: [PermissionKeys.MANAGE_MARKETING],
})

export default GuardedPlayLogsPage
